import { useEffect, useMemo, useRef } from "react";

import { useGetDealFilterValues } from "@vega-api/generated/deal/deal";
import type { GetDealOpportunitiesForLimitedPartnerParams } from "@vega-api/generated/types/getDealOpportunitiesForLimitedPartnerParams";
import { m } from "@vega-core/paraglide/messages";
import { trackCoInvestDealsFiltered } from "@vega-core/utils/posthog/coInvestAnalytics";
import { useTableFilters } from "@vega-modules/components/Table/TableFilters/hooks/useTableFilters";
import {
  FilterType,
  TableFilterId,
  type TableFiltersConfig,
} from "@vega-modules/components/Table/TableFilters/types";

export const useCoInvestmentFilters = () => {
  const { data: filterOptions, isLoading: isFilterOptionsLoading } =
    useGetDealFilterValues({
      query: {
        select: (response) => response.data,
      },
    });

  const filtersConfig = useMemo(
    () =>
      ({
        filters: [
          {
            id: "searchQuery",
            type: FilterType.Search,
            label: m.coInvestments_filters_search(),
            props: {
              placeholder: m.coInvestments_filters_searchPlaceholder(),
            },
          },
          // Temporarily commented out because not all GP (e.g. Apollo) will have these values: https://vegainvestments.atlassian.net/browse/VD-18007
          // {
          //   id: "assetClass",
          //   type: FilterType.Dropdown,
          //   label: m.coInvestments_filters_assetClass(),
          //   props: {
          //     placeholder: m.coInvestments_filters_allAssetClasses(),
          //     options: filterOptions?.assetClasses || [],
          //   },
          // },
          // {
          //   id: "region",
          //   type: FilterType.Dropdown,
          //   label: m.coInvestments_filters_region(),
          //   props: {
          //     placeholder: m.coInvestments_filters_allRegions(),
          //     options: filterOptions?.regions || [],
          //   },
          // },
          // {
          //   id: "sector",
          //   type: FilterType.Dropdown,
          //   label: m.coInvestments_filters_sector(),
          //   props: {
          //     placeholder: m.coInvestments_filters_allSectors(),
          //     options: filterOptions?.sectors || [],
          //   },
          // },
          {
            id: "activeOrPast",
            type: FilterType.Dropdown,
            label: m.coInvestments_filters_opportunityStatus(),
            props: {
              placeholder: m.coInvestments_filters_allOpportunities(),
              options: filterOptions?.activeOrPast || [],
            },
          },
          {
            id: "investedOrRejected",
            type: FilterType.Dropdown,
            label: m.coInvestments_filters_investmentStatus(),
            props: {
              placeholder: m.coInvestments_filters_allInvestmentStatuses(),
              options: filterOptions?.investedOrPassed || [],
            },
          },
        ],
        showResetButton: true,
      }) as const satisfies TableFiltersConfig,
    [filterOptions],
  );

  const { filterState, setFilterValue, RenderFilters, hasActiveFilters } =
    useTableFilters({
      id: TableFilterId.CoInvestments,
      config: filtersConfig,
      isLoading: isFilterOptionsLoading,
      syncWithURL: true,
      showSavedFilters: true,
      persistInSession: false,
    });

  const previousDropdownFilters = useRef<{
    activeOrPast: string | undefined;
    investedOrRejected: string | undefined;
  }>({ activeOrPast: undefined, investedOrRejected: undefined });

  useEffect(() => {
    const current = {
      activeOrPast: filterState.activeOrPast?.value,
      investedOrRejected: filterState.investedOrRejected?.value,
    };

    (["activeOrPast", "investedOrRejected"] as const).forEach((filter) => {
      if (
        current[filter] &&
        current[filter] !== previousDropdownFilters.current[filter]
      ) {
        trackCoInvestDealsFiltered({ filter });
      }
    });

    previousDropdownFilters.current = current;
  }, [filterState.activeOrPast?.value, filterState.investedOrRejected?.value]);

  const queryParams: Partial<GetDealOpportunitiesForLimitedPartnerParams> = {
    searchQuery: filterState.searchQuery,
    // Temporarily commented out because not all GP (e.g. Apollo) will have these values: https://vegainvestments.atlassian.net/browse/VD-18007
    // region: filterState.region?.value,
    // assetClass: filterState.assetClass?.value,
    // sector: filterState.sector?.value,
    activeOrPast: filterState.activeOrPast?.value,
    investedOrRejected: filterState.investedOrRejected?.value,
  };

  return {
    filterState,
    setFilterValue,
    RenderFilters,
    hasActiveFilters,
    queryParams,
  };
};
